import { call, put, select, takeEvery, takeLatest, cancelled } from 'redux-saga/effects';
import { get, isEmpty, merge } from 'lodash';
import { Router, i18n } from '../../src/i18n';
import * as API from '../../utils/API';
import {
  addUserPhone,
  checkUpdatePasswordKeyAction,
  createEntity,
  deleteEntity,
  deleteUserPhone,
  enqueueSnackbarAction,
  getActiveProfileAction,
  getAgenciesAction,
  getAggregationsAction,
  getArtistProfessionsAction,
  getCommonUserData,
  getCompaniesAction,
  getVenuesSearchAction,
  getList,
  getOne,
  getProfilePermissionsAction,
  getProfilesAction,
  getSettings,
  getUserPermissionsAction,
  getWholeList,
  loginUser as loginUserAction,
  resetPasswordAction,
  setActiveProfileAction,
  setUserData,
  setUserProfiles,
  updateOne,
  updatePasswordAction,
  updateUserData,
  getDynamicCountriesAction,
  getUserInfoAction,
  getActiveProfileSubscriptionsAction,
  updateStoreUserData,
} from './actions';

import route from '../../constants/routes';
import { selectAtistProfessions, selectUserData, selectUserProfiles } from './selectors';
import { selectCountryByCode } from '../../store/selectors';
import { formatResponseToEntity, formatResponseToList } from '../../utils/crudUtils';
import { getRegistrationsAction } from '../Authentification/Registration/actions';
import { ACCOUNT_SETTINGS, COUNTRIES_ENDPOINT } from '../../constants/endpointConstants';
import { fetchAbortController } from '../../utils/common';
import { TP } from '../../constants/consts';
import { VALID_ENTITY_FILTER } from '../ProductionUpdateSteps/constants';

export function* loginUser({ payload, isAfterRegister = false }) {
  try {
    const userData = yield call(API.login, payload.email, payload.password, payload.isTrustedDevice);
    const { data } = userData;
    yield put(loginUserAction.success({ id: get(data, 'id') }));
    if (payload?.callback) {
      payload.callback(get(data, 'id'));
    }

    if (!isAfterRegister) {
      if (get(data, 'goToSelectProfilePage')) {
        window.location.replace(`${route.SELECT_PROFILE}/${get(i18n, 'language', '').toLowerCase()}`);
      } else {
        const next = get(payload, 'nextPage', route.HOME);
        window.location.replace(next);
      }
    }
  } catch (err) {
    yield put(loginUserAction.failure(err.message));
    if (payload?.onRejected) {
      payload.onRejected(err);
    }
  } finally {
    yield put(loginUserAction.fulfill());
  }
}

export function* getUserFromData() {
  return yield select(selectUserData());
}

export function* fetchCommonUserData({ payload, serverCookies }) {
  try {
    const userToFetch = payload.user;
    if (!get(userToFetch, 'id', null)) {
      throw new Error('User undefined');
    }
    const id = userToFetch?.id;
    yield call(fetchUserData, id, serverCookies);
    yield call(fetchUserProfiles, id, serverCookies);
    yield call(fetchActiveProfile, {
      payload: { userId: id, activeProfileId: payload?.activeProfileId },
      serverCookies,
    });
    yield call(getSettingsSaga, id, serverCookies);
  } catch (err) {
    yield put(getCommonUserData.failure(err.message));
  } finally {
    yield put(getCommonUserData.fulfill());
  }
}

export function* getSettingsSaga(userId, serverCookies) {
  yield call(getListSaga, {
    payload: {
      endpoint: ACCOUNT_SETTINGS(userId),
      sagaRoutine: getSettings,
      params: { lang: 'en' },
    },
    serverCookies,
  });
}

export function* updateUserDataSaga({ payload, serverCookies }) {
  try {
    const user = yield call(getUserFromData);
    if (!get(user, 'id', null)) {
      throw new Error('User undefined');
    }
    const changes = { ...payload };
    delete changes.callback;
    const userData = yield call(API.updateUser, get(user, 'id'), changes, serverCookies);
    if (payload.callback) {
      yield call(payload.callback, { status: 'ok', data: get(userData, 'data') });
    }

    yield put(updateUserData.success(get(userData, 'data')));
  } catch (err) {
    if (payload.callback) {
      yield call(payload.callback, { status: 'error', data: err });
    }
    yield put(updateUserData.failure(err.message));
  } finally {
    yield put(updateUserData.fulfill());
    if (payload?.callback) {
      yield payload.callback();
    }
  }
}
export function* updateStoreUserDataSaga({ payload }) {
  try {
    yield put(updateUserData.success(payload));
  } catch (err) {
    yield put(updateUserData.failure(err.message));
  } finally {
    yield put(updateUserData.fulfill());
  }
}

export function* addUserPhoneSaga({ payload, serverCookies }) {
  try {
    const user = yield call(getUserFromData);
    const userId = get(user, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const userData = yield call(API.getUser, userId, serverCookies);
    const country = yield select(selectCountryByCode(get(payload, 'countryCode')));
    const phones = get(userData, 'data.phones', []);
    phones.push({
      country: {
        id: get(country, 'id'),
      },
      number: get(payload, 'phone'),
      isActive: phones.length === 0,
    });
    const updatedUserData = yield call(API.updateUser, userId, { phones }, serverCookies);
    yield put(addUserPhone.success(get(updatedUserData, 'data')));
  } catch (err) {
    yield put(addUserPhone.failure(err.message));
  } finally {
    yield put(addUserPhone.fulfill());
  }
}

export function* deleteUserPhoneSaga({ payload, serverCookies }) {
  try {
    const user = yield call(getUserFromData);
    const userId = get(user, 'id');
    if (!userId) {
      throw new Error('User undefined');
    }
    const userData = yield call(API.getUser, userId, serverCookies);
    const phones = get(userData, 'data.phones', []);
    const updatedUserData = yield call(
      API.updateUser,
      userId,
      {
        phones: phones.filter(phone => phone.id !== payload.id),
      },
      serverCookies,
    );
    yield put(deleteUserPhone.success(get(updatedUserData, 'data')));
  } catch (err) {
    yield put(deleteUserPhone.failure(err.message));
  } finally {
    yield put(deleteUserPhone.fulfill());
  }
}

export function* fetchUserData(userId, serverCookies) {
  try {
    const userData = yield call(API.getUser, userId, serverCookies);
    const { data } = userData;
    yield put(setUserData.success(data));
    yield call(getUserPermissions, userId, serverCookies);
  } catch (err) {
    yield put(setUserData.failure(err.message));
  } finally {
    yield put(setUserData.fulfill());
  }
}

export function* fetchUserProfiles(userId, serverCookies) {
  try {
    yield put(setUserProfiles.trigger());
    const userData = yield call(API.getProfiles, userId, true, true, serverCookies);
    const profiles = get(userData, 'data.data');
    yield put(setUserProfiles.success(profiles));
  } catch (err) {
    yield put(setUserProfiles.failure(err.message));
  } finally {
    yield put(setUserProfiles.fulfill());
  }
}

export function* fetchActiveProfile({ payload, serverCookies }) {
  try {
    const settingsData = yield call(API.getActiveProfile, payload.userId, {}, serverCookies);
    const userProfiles = yield select(selectUserProfiles());
    let activeProfileId = get(settingsData, 'data.value') || payload?.activeProfileId;

    if (!userProfiles || userProfiles.length === 0) {
      yield call(getProfilePermissions, null, serverCookies);
      throw new Error('No active profile available');
    }

    if (!userProfiles.find(up => up.id === activeProfileId)) {
      activeProfileId = get(userProfiles, '[0].id');
      // TODO Save active_id on api?
    }

    const activeProfile = yield call(API.getProfileData, activeProfileId, null, serverCookies);
    yield call(getProfilePermissions, activeProfileId, serverCookies);
    const subscriptionsData = yield call(
      API.getActiveProfileSubscriptions,
      payload.userId,
      activeProfileId,
      serverCookies,
    );

    yield put(getActiveProfileAction.success(activeProfile?.data));
    yield put(getActiveProfileSubscriptionsAction.success(get(subscriptionsData, 'data.data', [])));
  } catch (err) {
    yield put(getActiveProfileAction.failure(err.message));
  } finally {
    yield put(getActiveProfileAction.fulfill());
  }
}

export function* updateActiveProfile({ payload, serverCookies }) {
  try {
    const userData = yield call(getUserFromData);
    const userId = get(userData, 'id');
    const response = yield call(API.updateSettingsField, userId, 'active_profile_id', payload.profileId, serverCookies);
    const profileId = get(response, 'data.value', null);
    // Expecting to switch back to getting profile from profiles list. Temporary fix to accommodate api issues.
    // const activeProfileData = yield select(selecActiveProfileById(profileId));

    const activeProfileData = yield call(API.getProfileData, profileId, null, serverCookies);
    yield call(getProfilePermissions, get(activeProfileData, 'data.id'), serverCookies);
    yield put(setActiveProfileAction.success(activeProfileData.data));
    if (payload.path && payload.as) {
      Router.push(payload.path, payload.as);
    }
  } catch (err) {
    yield put(setActiveProfileAction.failure(err.message));
  } finally {
    yield put(setActiveProfileAction.fulfill());
  }
}

export function* getProfilePermissions(profileId, serverCookies) {
  try {
    if (!profileId) {
      yield put(getProfilePermissionsAction.failure('No profile Id'));
      return;
    }
    const response = yield call(API.getProfilePermissions, profileId, serverCookies);
    yield put(getProfilePermissionsAction.success(get(response, 'data.data', [])));
  } catch (err) {
    yield put(getProfilePermissionsAction.failure(err.message));
  } finally {
    yield put(getProfilePermissionsAction.fulfill());
  }
}

export function* getUserPermissions(userId, serverCookies) {
  try {
    const response = yield call(API.getUserPermissions, userId, serverCookies);
    yield put(getUserPermissionsAction.success(get(response, 'data.data', [])));
  } catch (err) {
    yield put(getUserPermissionsAction.failure(err.message));
  } finally {
    yield put(getUserPermissionsAction.fulfill());
  }
}

export function* resetPassword({ payload, serverCookies }) {
  try {
    yield call(API.resetUserPassword, payload, serverCookies);
    yield put(resetPasswordAction.success());
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.AS_RESET_PASSWORD_SENT_NOTIFICATION`, {}),
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(resetPasswordAction.failure(err.message));
  } finally {
    yield put(resetPasswordAction.fulfill());
  }
}

export function* updatePassword({ payload, serverCookies }) {
  try {
    yield call(API.updateUserPassword, payload.key, payload.password, serverCookies);
    yield put(updatePasswordAction.success());
  } catch (err) {
    yield put(updatePasswordAction.failure(err.message));
  } finally {
    yield put(updatePasswordAction.fulfill());
  }
}

export function* checkPasswordKey({ payload, serverCookies }) {
  try {
    yield call(API.checkPasswordKeyStatus, payload, serverCookies);
    yield put(checkUpdatePasswordKeyAction.success());
  } catch (err) {
    yield put(checkUpdatePasswordKeyAction.failure(err.message));
  } finally {
    yield put(checkUpdatePasswordKeyAction.fulfill());
  }
}

export function* fetchArtistProfessions({ payload, serverCookies }) {
  try {
    const { query } = payload;
    const professions = yield select(selectAtistProfessions());
    if (isEmpty(professions)) {
      const response = yield call(API.getArtistProfessions, query || payload, {}, serverCookies);
      yield put(getArtistProfessionsAction.success(get(response, 'data.data')));
    }
  } catch (err) {
    yield put(getArtistProfessionsAction.failure(err.message));
  } finally {
    yield put(getArtistProfessionsAction.fulfill());
  }
}

export function* fetchAggregations({ payload, serverCookies }) {
  const { query, pagination, endpoint } = payload;
  const abortController = fetchAbortController();
  const signal = abortController ? abortController.signal : null;
  try {
    const response = yield call(API.getAggregations, endpoint, query, pagination, signal, serverCookies);
    yield put(getAggregationsAction.success(get(response, 'data')));
  } catch (err) {
    if (yield cancelled() && abortController) {
      abortController.abort();
    }
    yield put(getAggregationsAction.failure(err.message));
  } finally {
    if (yield cancelled() && abortController) {
      // Cancel the API call if the saga was cancelled
      abortController.abort();
    }
    yield put(getAggregationsAction.fulfill());
  }
}

export function* fetchAgencies({ payload }) {
  try {
    const response = yield call(API.getAgencies, {
      query: payload.query || payload,
      validation_status: 'pending,processing,approved',
      ...get(payload, 'query', {}),
    });
    yield put(getAgenciesAction.success(get(response, 'data.data')));
  } catch (err) {
    yield put(getAgenciesAction.failure(err.message));
  } finally {
    yield put(getAgenciesAction.fulfill());
  }
}

export function* fetchCompanies({ payload, serverCookies }) {
  try {
    const response = yield call(API.getCompanies, payload.query, payload.pagination, serverCookies);
    yield put(getCompaniesAction.success(get(response, 'data.data')));
    if (payload.callback) {
      yield call(payload.callback, get(response, 'data.data'));
    }
  } catch (err) {
    yield put(getCompaniesAction.failure(err.message));
  } finally {
    yield put(getCompaniesAction.fulfill());
  }
}

export function* fetchVenues({ payload, serverCookies }) {
  try {
    const response = yield call(API.getVenues, payload.query, payload.pagination, serverCookies);
    yield put(getVenuesSearchAction.success(get(response, 'data.data')));
  } catch (err) {
    yield put(getVenuesSearchAction.failure(err.message));
  } finally {
    yield put(getVenuesSearchAction.fulfill());
  }
}

export function* fetchProfilesList({ payload }) {
  try {
    const response = yield call(
      API.searchProfiles,
      payload.type,
      payload.query,
      payload.pagination,
      payload.filters,
      payload.from,
    );
    yield put(getProfilesAction.success(get(response, 'data.data')));
    if (payload.callback) {
      yield call(payload.callback, get(response, 'data.data'));
    }
  } catch (err) {
    yield put(getProfilesAction.failure(err.message));
  } finally {
    yield put(getProfilesAction.fulfill());
  }
}

export function* getListSaga({ payload, serverCookies }) {
  const { endpoint, sagaRoutine, params, callback, notifyOnError } = payload;
  if (endpoint === null || sagaRoutine === null) {
    throw new Error('Specify endpoint and sagaRoutine');
  }
  yield put(sagaRoutine.trigger(payload));
  try {
    yield put(sagaRoutine.request());
    const response = yield call(API.getList, endpoint, params, serverCookies);
    const list = formatResponseToList(response);
    yield put(sagaRoutine.success(list));
    if (Boolean(callback) && typeof callback === 'function') callback(list);
  } catch (err) {
    if (notifyOnError) {
      yield put(
        enqueueSnackbarAction({
          message: i18n.t(`${TP}.FN_ERROR_FETCHING_DATA`, { endpoint, error: err?.responseText || err?.message }),
          options: {
            variant: 'error',
          },
        }),
      );
    }
    yield put(sagaRoutine.failure(err.message));
  } finally {
    yield put(sagaRoutine.fulfill());
  }
}

export function* getWholeListSaga({ payload, serverCookies }) {
  // TODO: Implement dynamic "get all" flow (/countries/?limit=0 => /countries/?limit=COUNT)
  const payloadWithPagination = merge(payload, { params: { pagination: { page: 1, limit: 10000 } } });
  yield call(getListSaga, { payload: payloadWithPagination, serverCookies });
}

export function* getOneSaga({ payload, serverCookies }) {
  const { endpoint, sagaRoutine, id, params } = payload;
  if (endpoint === null || sagaRoutine === null || id === null) {
    throw new Error('Specify endpoint, id, sagaRoutine');
  }
  yield put(sagaRoutine.trigger());
  try {
    yield put(sagaRoutine.request());
    const response = yield call(API.getOne, endpoint, id, params, serverCookies);
    const data = formatResponseToEntity(response);
    yield put(sagaRoutine.success(data));
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_FETCHING_DATA`, { endpoint, error: err?.responseText || err?.message }),
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(sagaRoutine.failure(err.message));
  } finally {
    yield put(sagaRoutine.fulfill());
  }
}

export function* updateOneSaga({ payload, serverCookies }) {
  const { endpoint, sagaRoutine, id, params, files, successMessage, errorMessage, callback } = payload;
  if (endpoint === null || id === null) {
    throw new Error('Specify endpoint, id, sagaRoutine');
  }
  if (sagaRoutine) yield put(sagaRoutine.trigger(payload));
  try {
    if (sagaRoutine) yield put(sagaRoutine.request());
    const response = yield call(API.updateOne, endpoint, id, params, files, serverCookies);
    const data = formatResponseToEntity(response);
    if (sagaRoutine) yield put(sagaRoutine.success(data));
    if (Boolean(callback) && typeof callback === 'function') callback(data?.entity);
    yield put(
      enqueueSnackbarAction({
        message: successMessage || i18n.t(`${TP}.FN_SUCCESS_UPDATING_DATA`, { endpoint }),
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message:
          errorMessage ||
          i18n.t(`${TP}.FN_ERROR_UPDATING_DATA`, { endpoint, error: err?.responseText ?? err?.message }),
        options: {
          variant: 'error',
        },
      }),
    );
    if (sagaRoutine) yield put(sagaRoutine.failure(err.message));
  } finally {
    if (sagaRoutine) yield put(sagaRoutine.fulfill());
  }
}

export function* deleteOneSaga({ payload, serverCookies }) {
  const { endpoint, sagaRoutine, id, params, successMessage, errorMessage, callback } = payload;
  if (endpoint === null || id === null) {
    throw new Error('Specify endpoint, id');
  }
  if (sagaRoutine) {
    yield put(sagaRoutine.trigger(payload));
  }
  try {
    if (sagaRoutine) {
      yield put(sagaRoutine.request());
    }
    yield call(API.deleteOne, endpoint, id, params, serverCookies);
    if (sagaRoutine) {
      yield put(sagaRoutine.success({ id }));
    }
    if (Boolean(callback) && typeof callback === 'function') callback(id);
    yield put(
      enqueueSnackbarAction({
        message: successMessage || i18n.t(`${TP}.FN_SUCCESS_DELETING_DATA`, { endpoint }),
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message:
          errorMessage ||
          i18n.t(`${TP}.FN_ERROR_DELETING_DATA`, { endpoint, error: err?.responseText || err?.message }),
        options: {
          variant: 'error',
        },
      }),
    );
    if (sagaRoutine) {
      yield put(sagaRoutine.failure(err.message));
    }
  } finally {
    if (sagaRoutine) {
      yield put(sagaRoutine.fulfill());
    }
  }
}

export function* createEntitySaga({ payload, serverCookies }) {
  const {
    endpoint,
    sagaRoutine,
    params,
    successMessage,
    errorMessageConverter,
    onError,
    errorMessage,
    callback,
    files,
    skipDefaultErrorMessage = false,
  } = payload;
  if (endpoint === null) {
    throw new Error('Specify endpoint, id');
  }
  if (sagaRoutine) {
    yield put(sagaRoutine.trigger(payload));
  }
  try {
    if (sagaRoutine) {
      yield put(sagaRoutine.request());
    }
    const response = yield call(API.create, endpoint, params, files, serverCookies);
    const data = formatResponseToEntity(response);
    if (sagaRoutine) {
      yield put(sagaRoutine.success(data));
    }

    if (Boolean(callback) && typeof callback === 'function') callback(data?.entity);

    if (!payload?.noSuccessMessage) {
      yield put(
        enqueueSnackbarAction({
          message: successMessage || i18n.t(`${TP}.FN_SUCCESS_CREATING_DATA`, { endpoint }),
          options: {
            variant: 'success',
          },
        }),
      );
    }
  } catch (err) {
    let httpMessage = err?.responseText?.message || err?.responseText || err?.message;

    if (typeof errorMessageConverter === 'function') {
      httpMessage = errorMessageConverter(err);
    }
    if (typeof onError === 'function') {
      onError(err);
    }

    if (!skipDefaultErrorMessage) {
      yield put(
        enqueueSnackbarAction({
          message: errorMessage || i18n.t(`${TP}.FN_ERROR_CREATING_DATA`, { endpoint, error: httpMessage }),
          options: {
            variant: 'error',
          },
        }),
      );
    }
    if (sagaRoutine) {
      yield put(sagaRoutine.failure(err.message));
    }
  } finally {
    if (sagaRoutine) {
      yield put(sagaRoutine.fulfill());
    }
  }
}

export function* fetchDynamicCountries({ payload, serverCookies }) {
  try {
    const response = yield call(
      API.getList,
      COUNTRIES_ENDPOINT,
      {
        queryParams: {
          validation_status: VALID_ENTITY_FILTER,
          ...(payload?.params || {}),
        },
      },
      serverCookies,
    );
    yield put(getDynamicCountriesAction.success(formatResponseToList(response)));
  } catch (err) {
    yield put(
      enqueueSnackbarAction({
        message: i18n.t(`${TP}.FN_ERROR_FETCHING_DATA`, {
          endpoint: COUNTRIES_ENDPOINT,
          error: err?.responseText || err?.message,
        }),
        options: {
          variant: 'error',
        },
      }),
    );
    yield put(getDynamicCountriesAction.failure(err.message));
  } finally {
    yield put(getDynamicCountriesAction.fulfill());
  }
}

export function* fetchUserInfo({ payload, serverCookies }) {
  try {
    const response = yield call(API.getUserInfo, payload?.id, serverCookies);
    const { data } = response;
    // set userDetails in redux
    const userDetails = get(data, 'userDetails.data', {});
    yield put(setUserData.success(userDetails));

    // set user permissions in redux
    const userPermissions = get(data, 'userPermissions.data.data', []);
    yield put(getUserPermissionsAction.success(userPermissions));

    // set user profiles in redux
    const userProfiles = get(data, 'userProfiles.data.data', []);
    yield put(setUserProfiles.success(userProfiles));

    // set active profile in redux
    const activeProfile = get(data, 'activeProfileData.data', {});
    yield put(getActiveProfileAction.success(activeProfile));

    // set active profile subscriptions in redux
    const subscriptionsData = yield call(
      API.getActiveProfileSubscriptions,
      payload?.id,
      get(activeProfile, 'id'),
      serverCookies,
    );
    yield put(getActiveProfileSubscriptionsAction.success(get(subscriptionsData, 'data.data')));

    // set Profile permissions in redux
    const profilePermissions = get(data, 'profilePermissions.data.data', []);
    yield put(getProfilePermissionsAction.success(profilePermissions));

    // set user settings in redux
    const userSettings = get(data, 'userSettings.data', {});
    yield put(getSettings.success(userSettings));

    // set user registrations in redux
    const userRegistrations = get(data, 'userRegistrations.data', []);
    yield put(getRegistrationsAction.success(userRegistrations));

    yield put(getUserInfoAction.success(data));
  } catch (err) {
    yield put(
      getUserInfoAction.failure({
        status: err.status,
        message: err.message,
      }),
    );
  } finally {
    yield put(getUserInfoAction.fulfill());
  }
}

export default function* appSaga() {
  yield takeLatest(loginUserAction.TRIGGER, loginUser);
  yield takeLatest(getCommonUserData.TRIGGER, fetchCommonUserData);
  yield takeLatest(updateUserData.TRIGGER, updateUserDataSaga);
  yield takeLatest(updateStoreUserData.TRIGGER, updateStoreUserDataSaga);
  yield takeLatest(addUserPhone.TRIGGER, addUserPhoneSaga);
  yield takeLatest(deleteUserPhone.TRIGGER, deleteUserPhoneSaga);
  yield takeLatest(setActiveProfileAction.TRIGGER, updateActiveProfile);
  yield takeLatest(resetPasswordAction.TRIGGER, resetPassword);
  yield takeLatest(updatePasswordAction.TRIGGER, updatePassword);
  yield takeLatest(checkUpdatePasswordKeyAction.TRIGGER, checkPasswordKey);
  yield takeLatest(getArtistProfessionsAction.TRIGGER, fetchArtistProfessions);
  yield takeEvery(getAggregationsAction.TRIGGER, fetchAggregations);
  yield takeLatest(getAgenciesAction.TRIGGER, fetchAgencies);
  yield takeLatest(getCompaniesAction.TRIGGER, fetchCompanies);
  yield takeLatest(getVenuesSearchAction.TRIGGER, fetchVenues);
  yield takeLatest(getProfilesAction.TRIGGER, fetchProfilesList);
  yield takeEvery(getList.TRIGGER, getListSaga);
  yield takeEvery(getOne.TRIGGER, getOneSaga);
  yield takeEvery(updateOne.TRIGGER, updateOneSaga);
  yield takeEvery(deleteEntity.TRIGGER, deleteOneSaga);
  yield takeEvery(createEntity.TRIGGER, createEntitySaga);
  yield takeEvery(getWholeList.TRIGGER, getWholeListSaga);
  yield takeLatest(getActiveProfileAction.TRIGGER, fetchActiveProfile);
  yield takeLatest(getDynamicCountriesAction.TRIGGER, fetchDynamicCountries);
  yield takeLatest(getUserInfoAction.TRIGGER, fetchUserInfo);
}
